import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  House, Robot, ListChecks, PlugsConnected, GearSix, SignOut,
  CaretDown, Lightning,
  Crosshair, FlowArrow, Globe, Briefcase, TreeStructure,
  ChartLine, Sparkle, Buildings, Command as CommandIcon,
  FileText, ClipboardText,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import CreditUsagePanel from './CreditUsagePanel';
import ToolsModal from './ToolsModal';
import { useUsageStatus, UsageWidget } from './UsageEnforcement';
import { TopUpModal } from './TopUpModal';

/* ─── Primary nav (discover → run → monitor) ─── */
const PRIMARY_ITEMS = [
  { icon: House, label: 'Home', path: '/dashboard' },
  { icon: Crosshair, label: 'Opportunities', path: '/opportunities' },
  { icon: Robot, label: 'Workers', path: '/workers' },
  { icon: ListChecks, label: 'Activity', path: '/inbox' },
  { icon: PlugsConnected, label: 'Tools', path: '/apps', modal: true },
  // Team removed — accessible via Settings
];

/* ─── Advanced items (Partner Pro / toggle) ─── */
const ADVANCED_ITEMS = [];

const ORG_ITEMS = [
  { icon: ClipboardText, label: 'Assessment', path: '/assessments' },
  { icon: FlowArrow, label: 'Processes', path: '/processes' },
  { icon: Briefcase, label: 'Job Descriptions', path: '/job-descriptions' },
  { icon: TreeStructure, label: 'Org Charts', path: '/org-chart' },
  { icon: FileText, label: 'Reports', path: '/reports' },
];

const ADVANCED_TAIL = [
  { icon: Globe, label: 'External Agents', path: '/external-agents' },
  { icon: ChartLine, label: 'Analytics', path: '/analytics' },
];

const ServiceSidebar = ({ isMobile, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, authReady } = useAuth();
  const { plan: tier } = usePlan();
  const { activeOrganization } = useOrganization();
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [orgExpanded, setOrgExpanded] = useState(false);
  const [showCreditPanel, setShowCreditPanel] = useState(false);
  const [showToolsModal, setShowToolsModal] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const { creditStatus, voiceStatus } = useUsageStatus();

  // Listen for global 'open-tools-modal' events (from OpportunityDetail, etc.)
  useEffect(() => {
    const handler = () => setShowToolsModal(true);
    window.addEventListener('open-tools-modal', handler);
    return () => window.removeEventListener('open-tools-modal', handler);
  }, []);

  const expanded = isMobile || pinned || hovered;
  const isPartnerPro = tier === 'partner_pro' || tier === 'partner-pro' || tier === 'enterprise' || tier === 'pro';

  // Auto-expand Advanced if user is on an Advanced route
  useEffect(() => {
    const advancedPaths = ['/processes', '/job-descriptions', '/org-chart', '/reports', '/external-agents', '/analytics', '/assessments'];
    const onAdvanced = advancedPaths.some(p => location.pathname.startsWith(p));
    if (onAdvanced) setAdvancedExpanded(true);
    const orgPaths = ['/assessments', '/processes', '/job-descriptions', '/org-chart'];
    if (orgPaths.some(p => location.pathname.startsWith(p))) setOrgExpanded(true);
  }, [location.pathname]);

  useEffect(() => {
    const fetchUnread = async () => {
      if (!activeOrganization?.organization_id) return;
      try {
        const res = await fetchWithOrg(
          `${BACKEND_URL}/api/inbox/unread-count`,
          {},
          activeOrganization.organization_id
        );
        if (res.ok) {
          const data = await res.json();
          setInboxCount(data.unread_count || 0);
        }
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [activeOrganization]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    if (onClose) onClose();
  };

  const handleNavClick = () => {
    if (isMobile && onClose) onClose();
  };

  const isActive = useCallback((path) => {
    const p = location.pathname;
    if (path === '/dashboard') return p === '/dashboard';
    if (path === '/opportunities') return p.startsWith('/opportunities');
    if (path === '/workers') return p.startsWith('/workers') || p.startsWith('/automations');
    if (path === '/inbox') return p.startsWith('/inbox');
    if (path === '/apps') return p === '/apps';
    if (path === '/partner-pro/admin/team-members') return p.startsWith('/partner-pro/admin/team-members');
    if (path === '/settings') return p.startsWith('/settings');
    if (path === '/processes') return p.startsWith('/processes');
    if (path === '/job-descriptions') return p.startsWith('/job-descriptions');
    if (path === '/org-chart') return p.startsWith('/org-chart');
    if (path === '/external-agents') return p.startsWith('/external-agents');
    if (path === '/analytics') return p.startsWith('/analytics');
    if (path === '/assessments') return p.startsWith('/assessments');
    return p === path;
  }, [location.pathname]);

  if (!authReady) {
    return (
      <div className={`h-full flex items-center justify-center ${isMobile ? 'w-full' : 'w-[68px]'} bg-[#111820]`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00E5C0]" />
      </div>
    );
  }

  const NavLink = ({ item, onClick }) => {
    const active = !item.modal && isActive(item.path);
    const Icon = item.icon;
    const badge = item.path === '/inbox' && inboxCount > 0 ? inboxCount : null;

    const cls = `group relative flex items-center ${expanded ? 'px-4 gap-3' : 'justify-center px-2'} py-2.5 rounded-lg transition-all duration-150 ${
      active
        ? 'bg-[rgba(0,229,192,0.08)] text-[#00E5C0]'
        : 'text-[#A8B2BF] hover:bg-[rgba(255,255,255,0.04)] hover:text-[#F0F4F8]'
    }`;

    const inner = (
      <>
        <span className="w-[18px] flex items-center justify-center shrink-0">
          <Icon size={18} weight={active ? 'fill' : 'regular'} />
        </span>
        {expanded && <span className="text-[13px] font-medium flex-1">{item.label}</span>}
        {badge && (
          <span className={`${expanded ? '' : 'absolute top-1 right-1'} min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-[#FF6B6B] text-white text-[9px] font-bold px-1`}>
            {badge}
          </span>
        )}
        {!expanded && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-[#0A0E14] border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
            {item.label}
          </div>
        )}
      </>
    );

    if (item.modal) {
      return (
        <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === 'Enter' && onClick?.()} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`} className={`block w-full cursor-pointer ${cls}`}>
          {inner}
        </div>
      );
    }

    return (
      <Link to={item.path} onClick={handleNavClick} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`} className={`block w-full ${cls}`}>
        {inner}
      </Link>
    );
  };

  return (
    <div
      data-testid="service-sidebar"
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      className={`h-full flex flex-col bg-[#111820] transition-all duration-200 ease-out overflow-hidden ${
        expanded
          ? (isMobile ? 'w-64 border-r border-[rgba(255,255,255,0.08)]' : 'w-56 absolute top-0 left-0 z-[201] shadow-[4px_0_24px_rgba(0,0,0,0.5)] border-r border-[rgba(255,255,255,0.1)]')
          : 'w-[68px] border-r border-[rgba(255,255,255,0.08)]'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center ${expanded ? 'px-5' : 'justify-center px-2'} h-14 border-b border-[rgba(255,255,255,0.08)]`}>
        <a href="/" className="flex items-center gap-3" onClick={handleNavClick}>
          <div className="w-8 h-8 rounded-lg bg-[#0A0E14] border border-[rgba(255,255,255,0.08)] flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img src="/man-and-machine-logo.png" alt="Logo" className="w-8 h-8 object-contain" />
          </div>
          {expanded && (
            <span className="text-sm font-bold text-[#F0F4F8] whitespace-nowrap" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              M <span className="text-[#00E5C0]">&</span> M
            </span>
          )}
        </a>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto overflow-x-hidden sidebar-scroll-hide">
        {/* Primary */}
        {PRIMARY_ITEMS.map((item) => (
          <NavLink key={item.path} item={item} onClick={item.modal ? () => setShowToolsModal(true) : undefined} />
        ))}

        {/* Advanced — collapsible, visible for Partner Pro */}
        {isPartnerPro && (
          <>
            <div className="pt-3 pb-1">
              {expanded ? (
                <button
                  onClick={() => setAdvancedExpanded(!advancedExpanded)}
                  data-testid="advanced-toggle"
                  className="w-full flex items-center justify-between px-4 text-[10px] font-semibold text-[#7D8896] uppercase tracking-widest hover:text-[#B0B8C4] transition-colors"
                >
                  <span>Advanced</span>
                  <CaretDown
                    size={10}
                    className={`transition-transform duration-200 ${advancedExpanded ? '' : '-rotate-90'}`}
                  />
                </button>
              ) : (
                <button
                  onClick={() => setAdvancedExpanded(!advancedExpanded)}
                  data-testid="advanced-collapsed-toggle"
                  className="w-full flex justify-center py-1.5 group"
                  title="Advanced"
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                    advancedExpanded
                      ? 'bg-[rgba(139,92,246,0.1)] text-[#A78BFA]'
                      : 'bg-[rgba(255,255,255,0.04)] text-[#7D8896] hover:text-[#A78BFA] hover:bg-[rgba(139,92,246,0.08)]'
                  }`}>
                    <Sparkle size={14} weight="bold" />
                  </div>
                </button>
              )}
            </div>

            {advancedExpanded && expanded && (
              <>
                {ADVANCED_ITEMS.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}

                {/* Organization sub-group */}
                <button
                  onClick={() => setOrgExpanded(!orgExpanded)}
                  data-testid="org-group-toggle"
                  className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.04)] hover:text-[#F0F4F8] transition-all duration-150"
                >
                  <div className="flex items-center gap-3">
                    <Buildings size={18} />
                    <span className="text-[13px] font-medium">Organization</span>
                  </div>
                  <CaretDown
                    size={12}
                    className={`transition-transform duration-200 ${orgExpanded ? '' : '-rotate-90'}`}
                  />
                </button>

                {orgExpanded && ORG_ITEMS.map((item) => (
                  <div key={item.path} className="pl-3">
                    <NavLink item={item} />
                  </div>
                ))}

                {ADVANCED_TAIL.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </>
            )}
          </>
        )}

        {/* Settings — always last */}
        <div className="pt-2">
          <NavLink item={{ icon: GearSix, label: 'Settings', path: '/settings' }} />
        </div>
      </nav>

      {/* Bottom */}
      <div className="py-3 px-2 border-t border-[rgba(255,255,255,0.08)] space-y-1">
        {/* Command — bottom control surface */}
        <button
          onClick={() => {
            window.dispatchEvent(new CustomEvent('toggle-command-drawer'));
            handleNavClick();
          }}
          data-testid="nav-command"
          className={`group relative flex items-center w-full ${expanded ? 'px-4 gap-3' : 'justify-center px-2'} py-2.5 rounded-lg bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.12)] text-[#00E5C0] hover:bg-[rgba(0,229,192,0.12)] transition-all duration-150 mb-1`}
        >
          <CommandIcon size={18} weight="bold" />
          {expanded && <span className="text-[13px] font-semibold flex-1 text-left">Command</span>}
          {expanded && <span className="text-[10px] text-[#7D8896] font-mono">&#8984;K</span>}
          {!expanded && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-[#0A0E14] border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
              Command &#8984;K
            </div>
          )}
        </button>

        {/* Usage Widget */}
        <UsageWidget
          creditStatus={creditStatus}
          voiceStatus={voiceStatus}
          expanded={expanded}
          onTopUp={() => setShowTopUp(true)}
        />

        <button
          onClick={handleLogout}
          data-testid="sidebar-logout"
          className={`w-full flex items-center ${expanded ? 'px-4 gap-3' : 'justify-center px-2'} py-2.5 rounded-lg text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.04)] hover:text-[#F0F4F8] transition-all duration-150`}
        >
          <SignOut size={18} />
          {expanded && <span className="text-[13px] font-medium">Sign out</span>}
        </button>
      </div>

      {/* Credit Usage Panel */}
      <CreditUsagePanel
        isOpen={showCreditPanel}
        onClose={() => setShowCreditPanel(false)}
      />

      {/* Tools Modal */}
      <ToolsModal isOpen={showToolsModal} onClose={() => setShowToolsModal(false)} />

      {/* Top Up Modal */}
      <TopUpModal
        isOpen={showTopUp}
        onClose={() => setShowTopUp(false)}
        defaultTab={voiceStatus?.blocked ? 'voice' : 'credits'}
      />
    </div>
  );
};

export default ServiceSidebar;
